import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';

/**
 * DTO que representa una liquidación VD individual en la respuesta
 * Actualizado para seguir el mismo patrón exitoso de liquidaciones-agencia
 */
export class LiquidacionVdItemDto {
  @ApiProperty({ example: 66 })
  id!: number;

  @ApiProperty({ example: '66-2024-11' })
  numeroLiquidacion!: string;

  @ApiProperty({ example: 66 })
  clienteId!: number;

  @ApiProperty({ example: 'ACOGRAPES' })
  cliente!: string;

  @ApiProperty({ example: 'Noviembre 2024' })
  mesAnio!: string;

  @ApiProperty({ example: 'Pagado' })
  estado!: string;

  @ApiProperty({ example: 4 })
  cntr!: number;

  @ApiProperty({ example: 10 })
  vd!: number;

  @ApiProperty({ example: 2 })
  descNeto!: number;

  @ApiProperty({ example: 12 })
  neto!: number;

  @ApiProperty({ example: 2 })
  iva!: number;

  @ApiProperty({ example: 14 })
  brutoFactura!: number;

  @ApiProperty({ example: 2 })
  instFinanciera!: number;

  @ApiProperty({ example: 12 })
  totalPagar!: number;

  @ApiProperty({ example: ['VER', 'PDF', 'BD'] })
  acciones!: string[];

  @ApiProperty({ example: 'OPB', description: 'Centro de costo (OPB o A&O)' })
  centroCosto!: string;

  // Campos adicionales opcionales para compatibilidad
  @ApiPropertyOptional({ example: '2024-2025' })
  temporada?: string;

  @ApiPropertyOptional({ example: 'APRIL' })
  mesEtd?: string;

  @ApiPropertyOptional({ example: 4 })
  cantidad?: number;

  @ApiPropertyOptional({ example: '2024-11-01T03:00:00.000Z' })
  fechaCreacion?: Date;
}

/**
 * DTO que representa los totales de la temporada
 */
export class LiquidacionVdTotalesDto {
  @ApiProperty({ example: 24 })
  totalCntr!: number;

  @ApiProperty({ example: 60 })
  totalVd!: number;

  @ApiProperty({ example: 12 })
  totalDescNeto!: number;

  @ApiProperty({ example: 72 })
  totalNeto!: number;

  @ApiProperty({ example: 12 })
  totalIva!: number;

  @ApiProperty({ example: 84 })
  totalBrutoFactura!: number;

  @ApiProperty({ example: 12 })
  totalInstFinanciera!: number;

  @ApiProperty({ example: 72 })
  totalPagar!: number;
}

/**
 * DTO que representa la respuesta paginada de liquidaciones VD
 * ✅ CORREGIDO: Usando la misma estrategia exitosa de liquidaciones-agencia
 */
export class LiquidacionVdResponseDto {
  @ApiProperty({
    description: 'Lista de liquidaciones VD para la página actual',
    type: 'array',
    items: { type: 'object' },
    example: [
      {
        numeroLiquidacion: '66-2024-11',
        mesAnio: 'Noviembre 2024',
        estado: 'Pagado',
        cntr: 4,
        vd: 10,
        descNeto: 2,
        neto: 12,
        iva: 2,
        brutoFactura: 14,
        instFinanciera: 2,
        totalPagar: 12,
        acciones: ['VER', 'PDF', 'BD'],
        id: 66,
        clienteId: 66,
        cliente: 'ACOGRAPES',
        centroCosto: 'OPB',
      },
    ],
  })
  items!: any[];

  @ApiProperty({ example: 6 })
  total!: number;

  @ApiProperty({ example: 1 })
  page!: number;

  @ApiProperty({ example: 10 })
  limit!: number;

  @ApiProperty({ example: 1 })
  totalPages!: number;

  @ApiProperty({ example: false })
  hasPrevPage!: boolean;

  @ApiProperty({ example: false })
  hasNextPage!: boolean;

  @ApiProperty({
    description: 'Totales calculados para todas las liquidaciones VD',
    example: {
      totalCntr: 24,
      totalVd: 60,
      totalDescNeto: 12,
      totalNeto: 72,
      totalIva: 12,
      totalBrutoFactura: 84,
      totalInstFinanciera: 12,
      totalPagar: 72,
    },
  })
  totales?: LiquidacionVdTotalesDto;
}

/**
 * DTO para la respuesta del endpoint de datos del PDF de liquidación VD.
 * Incluye todos los datos necesarios para generar el PDF en el frontend.
 *
 * Uso: Respuesta del endpoint GET /api/v1/liquidaciones-vd/:id/pdf-data.
 */
export class LiquidacionVdPdfDataResponseDto {
  /**
   * Datos completos para generar el PDF de liquidación VD.
   */
  @ApiProperty({
    type: Object,
    description: 'Datos completos para generar el PDF de liquidación VD',
  })
  data!: any;

  /**
   * Mensaje de éxito.
   */
  @ApiProperty({
    example: 'Datos del PDF de liquidación VD obtenidos exitosamente',
    description: 'Mensaje de confirmación del proceso',
  })
  message!: string;

  /**
   * Metadatos adicionales del PDF.
   */
  @ApiProperty({
    description: 'Metadatos adicionales para la generación del PDF',
    example: {
      version: '1.0',
      fechaGeneracion: '2024-11-15T10:30:00Z',
      tipoReporte: 'liquidacion-vd',
      incluyeSeguro: true,
    },
  })
  metadata?: {
    version: string;
    fechaGeneracion: Date;
    tipoReporte: string;
    incluyeSeguro: boolean;
  };
}

/**
 * DTO para la respuesta del cálculo de valor declarado.
 *
 * Uso: Respuesta del endpoint GET /api/v1/liquidaciones-vd/:id/valor-declarado.
 */
export class LiquidacionVdValorDeclaradoResponseDto {
  /**
   * ID de la liquidación VD.
   */
  @ApiProperty({
    description: 'ID de la liquidación VD',
  })
  liquidacionId!: number;

  /**
   * Cálculo detallado del valor declarado.
   */
  @ApiProperty({
    type: Object,
    description: 'Cálculo detallado del valor declarado',
  })
  valorDeclarado!: {
    total: number;
    totalItems: number;
    promedioItem: number;
  };

  /**
   * Información del seguro calculado.
   */
  @ApiProperty({
    description:
      'Información del seguro calculado basado en el valor declarado',
    example: {
      calculado: 25000,
      porcentaje: 2.0,
      recomendado: true,
      poliza: 'POL-VD-2024-001234',
    },
  })
  seguro!: {
    calculado: number;
    porcentaje: number;
    recomendado: boolean;
    poliza?: string;
  };

  /**
   * Análisis de riesgo basado en el valor.
   */
  @ApiProperty({
    description: 'Análisis de riesgo basado en el valor declarado',
    example: {
      nivel: 'Alto',
      recomendaciones: [
        'Verificar cobertura completa',
        'Documentar estado detallado',
      ],
    },
  })
  riesgo!: {
    nivel: string;
    recomendaciones: string[];
  };

  /**
   * Mensaje de éxito.
   */
  @ApiProperty({
    example: 'Cálculo de valor declarado completado exitosamente',
    description: 'Mensaje de confirmación del proceso',
  })
  message!: string;
}

/**
 * DTO para la respuesta del detalle de seguro VD.
 *
 * Uso: Respuesta del endpoint GET /api/v1/liquidaciones-vd/:id/detalle-seguro.
 */
export class LiquidacionVdDetalleSeguroResponseDto {
  /**
   * ID de la liquidación VD.
   */
  @ApiProperty({
    description: 'ID de la liquidación VD',
  })
  liquidacionId!: number;

  /**
   * Información de la póliza de seguro.
   */
  @ApiProperty({
    type: Object,
    description: 'Información completa de la póliza de seguro',
  })
  poliza!: {
    numero: string;
    compania: string;
    vigencia: {
      desde: string;
      hasta: string;
    };
    cobertura: {
      tipo: string;
      limite: number;
      deducible: number;
    };
    prima: {
      tasa: number;
      monto: number;
      moneda: string;
    };
  };

  /**
   * Detalles de cobertura.
   */
  @ApiProperty({
    description: 'Detalles de lo que incluye y excluye la cobertura',
    example: {
      incluye: ['Pérdida total', 'Robo', 'Incendio'],
      excluye: ['Guerra', 'Vicio propio', 'Demora'],
    },
  })
  cobertura!: {
    incluye: string[];
    excluye: string[];
  };

  /**
   * Valor declarado total asegurado.
   */
  @ApiProperty({
    description: 'Valor declarado total que está asegurado',
  })
  valorDeclarado!: number;

  /**
   * Recomendaciones relacionadas con el seguro.
   */
  @ApiProperty({
    description: 'Recomendaciones para mantener la cobertura',
    example: [
      'Verificar embalaje',
      'Documentar estado inicial',
      'Mantener documentación actualizada',
    ],
  })
  recomendaciones!: string[];

  /**
   * Mensaje de éxito.
   */
  @ApiProperty({
    example: 'Detalle de seguro obtenido exitosamente',
    description: 'Mensaje de confirmación del proceso',
  })
  message!: string;
}
